import React, { useCallback, useMemo, useState } from 'react'
import { DNABox, DNAButton, DNASlider } from '@alucio/lux-ui'
import PresentationEditor from './PresentationEditor/PresentationEditor'
import PresentationBuilderStateProvider, { usePresentationBuilderState } from './state/PresentationBuilderStateProvider'
import PresentationSelector from './PresentationSelector/PresentationSelector'
import { useSelector } from 'react-redux'
import { RootState } from 'src/state/redux'
import { presentationBuilderActions } from 'src/state/redux/slice/PresentationBuilder/PresentationBuilder'
import { StyleProp, useWindowDimensions, ViewStyle } from 'react-native'
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors'
import PresentationSelectorStateProvider, { usePresentationSelector }
  from './PresentationSelector/PresentationSelectorStateProvider'

import CloneBuilder, { POOL_CONTAINER_ID } from 'src/components/DnD/Clone/CloneBuilder'
import { Active, DndMonitorListener, useDndMonitor } from '@dnd-kit/core'
import SlidesGroupOverlay from 'src/components/DnD/Clone/PresentationBuilder/SlidesGroupOverlay'
import { TargetItem } from 'src/components/DnD/DnDWrapper'
import DocumentSearchProvider from 'src/hooks/useDocumentSearchV2'
import { DNADocumentFiltersProvider } from 'src/components/DNA/Document/DNADocumentFilters'
import QuickFiltersProvider from 'src/components/QuickFilters/QuickFiltersProvider'

interface PresentationBuilderProps {
}

export const PresentationBuilderPanels: React.FC<{
  compact: boolean,
  isSelectionMode: boolean,
  editorMaxWidth?: number,
  selector: React.ReactNode,
  editor: React.ReactNode,
}> = ({ compact, isSelectionMode, editorMaxWidth, selector, editor }) => {
  const [mobilePanel, setMobilePanel] = useState<'content' | 'presentation'>('content')
  const useMobilePanels = compact && isSelectionMode

  return (
    <DNABox fill appearance="col" style={{ minWidth: 0 }}>
      {useMobilePanels && (
        <DNABox appearance="row" spacing="sm" style={{ padding: 8 }}>
          <DNAButton
            appearance={mobilePanel === 'content' ? 'filled' : 'outline'}
            status={mobilePanel === 'content' ? 'primary' : 'tertiary'}
            size="sm"
            onPress={() => setMobilePanel('content')}
          >
            Add content
          </DNAButton>
          <DNAButton
            appearance={mobilePanel === 'presentation' ? 'filled' : 'outline'}
            status={mobilePanel === 'presentation' ? 'primary' : 'tertiary'}
            size="sm"
            onPress={() => setMobilePanel('presentation')}
          >
            Presentation
          </DNAButton>
        </DNABox>
      )}
      <DNABox fill appearance="row" style={{ minWidth: 0 }}>
        <DNABox
          fill
          style={{
            display: !isSelectionMode || (useMobilePanels && mobilePanel !== 'content') ? 'none' : undefined,
            minWidth: 0,
          }}
        >
          {selector}
        </DNABox>
        <DNABox
          fill
          style={{
            display: useMobilePanels && mobilePanel !== 'presentation' ? 'none' : undefined,
            maxWidth: useMobilePanels ? undefined : editorMaxWidth,
            minWidth: 0,
          }}
        >
          {editor}
        </DNABox>
      </DNABox>
    </DNABox>
  )
}

const DndMonitor = React.memo(() => {
  const { setActive } = usePresentationSelector()
  const dndListeners = useMemo<DndMonitorListener>(
    () => ({
      onDragStart(event) {
        setActive(event.active)
      },
      onDragEnd() {
        setActive(undefined)
      },
    }),
    [],
  )
  useDndMonitor(dndListeners)
  return null
})

const PresentationBuilderComponents: React.FC = () => {
  const { width } = useWindowDimensions()
  const containerStyle:StyleProp<ViewStyle> = {
    backgroundColor: colors['color-text-white'],
  }
  const {
    poolItems,
    targetItems,
    selectedItemIds,
    getGroupDrafts,
    handleSelectGroup,
    selectedGroups,
    handleGroupAdd,
    rearrangeEditorSlides,
  } = usePresentationSelector()

  const {
    selectedTargetItems,
    setSelectedTargetItems,
    builderMode,
    compliancePanelVisible,
  } = usePresentationBuilderState();
  const isSelectionMode = builderMode === 'selection'

  const allGroups = getGroupDrafts()
  const useRef = React.useRef<Active | undefined>()

  const onDragEndChanged = useCallback((targetItems: { [key: string]: TargetItem[] }) => {
    // After finished dragging, sync targetItems. Determine if we are adding or reordering...
    // Drag interactions can never remove an item from target
    const addingItemToTarget = targetItems.default.length > selectedGroups.length
    const reorderingTargetItems = targetItems.default.length === selectedGroups.length
    if (addingItemToTarget) {
      // Add items drag from pool
      let insertingIndex: number | undefined
      for (let i = 0; i < targetItems.default.length; i++) {
        const targetItem = targetItems.default[i]
        const isFound = selectedGroups.find(payloadGroup => payloadGroup.id === targetItem.itemId)
        if (!isFound) {
          insertingIndex = i
          break
        }
      }
      handleGroupAdd(insertingIndex)
    }
    // Rearrange editor slide order
    else if (reorderingTargetItems) rearrangeEditorSlides(targetItems.default, useRef.current)

    useRef.current = undefined
  }, [selectedGroups, handleGroupAdd, rearrangeEditorSlides])

  const onDragStart = useCallback((active: Active) => {
    if (active.data.current?.containerId === POOL_CONTAINER_ID) {
      // Mark the current dragging item as checked (if it is not already checked)
      const currentDraggingItemId = active.data.current?.itemId
      const currentDraggingGroup = allGroups.find(({ id }) => id === currentDraggingItemId)
      currentDraggingGroup && handleSelectGroup(currentDraggingGroup, true)
    }
    // TODO: i have tried to use the containerId to determine if the dragging item is from pool or target
    //  but it seems that when i use a different container id i get issues on the useCloneBuilder hook
    else if (active.data.current?.containerId === 'default') {
      const currentDraggingItemId = active.data.current?.itemId as string

      // add if does not exist into the array selectedTargetItems
      if (!selectedTargetItems.includes(currentDraggingItemId)) {
        setSelectedTargetItems([...selectedTargetItems, currentDraggingItemId])
      }
    }
    useRef.current = active
  }, [handleSelectGroup, allGroups])

  const targetItemsObj = useMemo(() => {
    return { default: targetItems }
  }, [targetItems])

  return (
    <DNABox
      fill
      style={containerStyle}
    >
      <CloneBuilder
        poolItems={poolItems}
        targetItems={targetItemsObj}
        selectedItemIds={selectedItemIds}
        onDragEndChanged={onDragEndChanged}
        onDragStart={onDragStart}
      >
        <PresentationBuilderPanels
          compact={width < 700}
          isSelectionMode={isSelectionMode}
          editorMaxWidth={isSelectionMode ? (compliancePanelVisible ? 650 : 600) : undefined}
          selector={<PresentationSelector />}
          editor={<PresentationEditor />}
        />
        <SlidesGroupOverlay />
        <DndMonitor />
      </CloneBuilder>
    </DNABox>
  )
}

const PresentationBuilder:React.FC<PresentationBuilderProps> = () => {
  const globalBuilderState = useSelector((state: RootState) => state.PresentationBuilder)

  const key = globalBuilderState.aiImport?.requestId ?? globalBuilderState.customDeckId ?? 'new';
  // Closing clears aiImport; retain the provider key until the slider finishes closing.
  const [sessionKey, setSessionKey] = useState<string>();
  if (globalBuilderState.visible && sessionKey !== key) setSessionKey(key);

  return (
    <DNASlider
      visible={globalBuilderState.visible}
      onInvisible={() => { if (!globalBuilderState.visible) setSessionKey(undefined); }}
      setVisible={() => presentationBuilderActions.closePresentationBuilder()}
    >
      {sessionKey !== undefined && <PresentationBuilderStateProvider
        key={sessionKey}
      >
        <DocumentSearchProvider>
          <DNADocumentFiltersProvider>
            <QuickFiltersProvider quickFilters={[]}>
              <PresentationSelectorStateProvider>
                <PresentationBuilderComponents/>
              </PresentationSelectorStateProvider>
            </QuickFiltersProvider>
          </DNADocumentFiltersProvider>
        </DocumentSearchProvider>
      </PresentationBuilderStateProvider>}
    </DNASlider>
  )
}

export default PresentationBuilder
